import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  IconButton,
  Snackbar,
  Slide,
  Typography,
  CircularProgress,
  Button,
  useMediaQuery,
} from '@mui/material';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/diagram-js.css';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import BpmnJS from 'bpmn-js/lib/Viewer';
import Canvas from 'diagram-js/lib/core/Canvas';
import ElementRegistry from 'diagram-js/lib/core/ElementRegistry';
import EventBus from 'diagram-js/lib/core/EventBus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGraphQLQuery } from '../../customHooks/useGraphQL';
import { GET_AI_ENABLER_DATA, GET_AI_SOLUTIONS_DATA } from '../../graphql/queries/commonQueries';
import { saveDiagram } from '../../services/diagramService';
import { closeDiagram, refreshTaxonomy } from '../../slices/diagramViewerSlice';
import { updateIdeationFieldState } from '../../slices/ideationSlice/newIdeationSlice';
import { updateDiagramData } from '../../slices/diagramViewerSlice';
import { setOpportunities } from '../../slices/SolutionsSlice/AiOpportunitiesSlice';
import TechnologyDataLandscape from '../EnterpriseContext';
import SolutionBreadcrumb from '../SolutionXPLR/SolutionBreadcrumb';
import TaxonomyBreadcrumb from '../TaxonomyXPLR/TaxonomyBreadcrumb';
import DiagramAiComponentStepHover from './DiagramAiComponentStepHover';
import DiagramModal from './DiagramModel';
import { DiagramToolbar } from './DiagramToolbar';
import { ZoomControls } from './DiagramZoomControl';
import FooterActions from './FooterActions';
import { useDiagramDrag } from './UseDiagramDrag';
import CloseIcon from '@mui/icons-material/Close';
import DataTable from '../../common/components/DataTable/DataTable';
import ShowToast, { ToastTypeEnum } from '../../common/components/ShowToast';
import ViewModal from '../SimulationXPLR/ViewModal';
import DiagramInfoModal from './EditModal';
import { useNavigate } from 'react-router-dom';
import CustomModal from '../../common/components/Modal/CustomModal';
import Modal from '../../common/components/Modal/Modal';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { UPDATE_FAVORITE_AI_SOLUTIONS } from '../../graphql/mutations/common';
import { useGraphQLMutation } from '../../customHooks/useGraphQL';
import { httpService } from '../../utility/httpService';
import { httpConstants } from '../../constants';
import ViewModalOld from '../SimulationXPLR/ViewModal1';
import { API_BASE, API_GRAPH_BASE } from '../../config/env';
import { AiSolutionData } from '../TaxonomyXPLR/taxonomyXPLR';
import { CloseButton } from './SharedStyle';
import ProcessInfoSlide from './ProcessInfoSlide';
import ConfirmationModal from '../../common/components/ConfirmationModal';
import { useNavigationGuard } from '../../common/hooks/useNavigationGuard';
// import SolutionActionButtons from '../SimulationXPLR/SolutionActionButtons';
import type { AiOpportunity } from '../../slices/SolutionsSlice/AiOpportunitiesInterface';
import DownloadProgressSnackbar from '../../common/components/DownloadProgressSnackbar';
import {
  convertBpmnXmlToBase64,
  generateWordWithAccurateTOC,
  updateWordDocLinks,
} from '../../utility/reportUtils';
import type { Enhancement } from '../NewIdeationXPLR/NewIdeation/proposedEnhancements';
import { fetchAISolutionWithAgentEnablement } from '../../utility/aiOpportunityUtils';
import { REPORT_PROCESS_DIAGRAM_REPLACABLE_LINK } from '../../appConstants';

export interface DiagramData {
  diagram: {
    id: string;
    xml: string;
    data: {
      [key: string]: {
        name: string;
        description: string;
      };
    };
  };
}

export type Opportunity = AiOpportunity;

interface ElementData {
  id: string;
  type: string;
  name: string;
  description?: string;
  aiEnablers?: string[];
}

interface ElementModal {
  isOpen: boolean;
  elementData: ElementData | null;
  activeTab: 'aiHubble' | 'opportunities';
  opportunities?: Opportunity[];
}

interface Technology {
  id: string;
  name: string;
}

interface DiagramBpmnProps {
  diagramData: DiagramData | null;
  aiOpportunityData?: Opportunity[];
  isEditPermission?: boolean;
  selectedTaxonomy?: any;
  selectedE2E?: any;
  selectedSubprocess?: any;
  selectedCard?: any;
  selectedStage?: any;
  selectedSolution?: any;
  selectedSolutionDescription?: any;
  taxonomySubprocessName?: any;
  taxonomySubprocessDescription?: any;
  onResetAll?: () => void;
  onResetTaxonomy?: () => void;
  onResetE2E?: () => void;
  onResetSolution?: () => void;
  useCaseData?: any;
  useCaseDiagramDetails?: any;
  kpis?: any;
  onDiagramSaved?: (xml: string, data: any) => void;
  isProcessDiagram?: boolean;
  opportunityName?: string;
  onClose?: () => void;
  // handleIdeationIntake?: () => void;
  onDiagramChange?: (hasChanges: boolean) => void;
  lastSavedXmlRef?: any;
  prevDiagramData?: any;
  setPrevDiagramData?: (data: any) => void;
}

interface ApiResponse<T = any> {
  success?: boolean;
  status?: number;
  error?: string;
  data?: T;
}
export interface DiagramBpmnHandle {
  saveDiagram: () => Promise<void>;
  discardChanges: () => Promise<void> | void;
}

const DiagramBpmn: React.FC<DiagramBpmnProps> = ({
  diagramData,
  aiOpportunityData = [],
  isEditPermission = true,
  selectedTaxonomy,
  selectedE2E,
  selectedSubprocess,
  selectedCard,
  selectedStage,
  selectedSolution,
  selectedSolutionDescription,
  taxonomySubprocessName,
  taxonomySubprocessDescription,
  onResetAll,
  onResetTaxonomy,
  onResetSolution,
  onResetE2E,
  useCaseData,
  onDiagramSaved,
  isProcessDiagram = false,
  opportunityName,
  onClose,
  // useCaseDiagramDetails,
  kpis = {},
  // handleIdeationIntake,
  onDiagramChange,
  lastSavedXmlRef = {},
  prevDiagramData = {},
  setPrevDiagramData = () => {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const bpmnModelerRef = useRef<BpmnJS | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ElementModal>({
    isOpen: false,
    elementData: null,
    activeTab: 'aiHubble',
    opportunities: [],
  });

  const [showAllAiComponents, setShowAllAiComponents] = useState<boolean>(false);
  const [hoveredElements, setHoveredElements] = useState<Set<string>>(new Set());
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState<boolean>(false);
  const [currentScale, setCurrentScale] = useState(1);
  const [, setSavingDiagram] = useState(false);
  const [, setSaveSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [opportunityUpdated, setOpportunityUpdated] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<null | 'aiSolution' | 'processInfo' | 'diagram'>(
    null
  );
  const [isDiagramChanged, setIsDiagramChanged] = useState(false);
  const [, setIsSaved] = useState(false);
  const [, setIsStepDescriptionUpdated] = useState(false);
  const isProcessXPLRActive = window.location.pathname.includes('process-xplr');

  const {
    isModalOpen: isNavigationGuardModalOpen,
    confirmNavigation,
    cancelNavigation,
  } = useNavigationGuard({
    shouldBlock: isDiagramChanged,
  });

  const {
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useDiagramDrag({
    bpmnModelerRef,
    currentScale,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [renameProcessName, setRenameProcessName] = useState<string>('');
  const currentOrg = localStorage.getItem('current_organization') || 'Empty';
  const [isFitViewToggled, setIsFitViewToggled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeleteIdeationModal, setShowDeleteIdeationModal] = useState<boolean>(false);
  const [solutionData, setSolutionData] = useState([]);
  const [selectedSolutionFromLibrary, setSelectedSolutionFromLibrary] = useState<{
    id: string;
    name: string;
    vault?: boolean;
  }>({
    id: '',
    name: '',
  });
  const [aiSolutionData, setAiSolutionData] = useState<AiSolutionData | null>(null);

  const [showVaultModal, setShowVaultModal] = useState<boolean>(false);
  const handleCloseVaultModal = () => setShowVaultModal(false);

  const [favoriteModalOpen, setFavoriteModalOpen] = useState(false);
  const [favoritePending, setFavoritePending] = useState(false);
  const [favoriteTarget] = useState<{ id: string; name: string; favorite?: boolean } | null>(null);
  const [, setFavoriteStatusMap] = useState<Record<string, boolean>>({});
  const [showApplySuccessSnackbar, setShowApplySuccessSnackbar] = useState(false);
  const { mutate: mutateFavorite } = useGraphQLMutation(UPDATE_FAVORITE_AI_SOLUTIONS, {
    onCompleted: () => {
      setFavoritePending(false);
      setFavoriteModalOpen(false);
      setShowApplySuccessSnackbar(true);
    },
    onError: () => {
      // Revert optimistic update
      if (favoriteTarget) {
        setFavoriteStatusMap((prev) => ({
          ...prev,
          [favoriteTarget.id]: !prev[favoriteTarget.id],
        }));
      }
      setFavoritePending(false);
      setFavoriteModalOpen(false);
    },
  });
  const [enterpriseContextApplied, setEnterpriseContextApplied] = useState<boolean>(false);
  const navigate = useNavigate();
  const [loadingDocument, setLoadingDocument] = useState<boolean>(false);
  const [downloadProgressOpen, setDownloadProgressOpen] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);

  const useCaseId = useSelector((state: RootState) => state.newIdeation.id);
  const selectedCardUseCaseId =
    selectedCard?.useCaseId || selectedCard?.use_case_id || useCaseData?.id;

  // Sync vault status between aiSolutionData and selectedSolutionFromLibrary
  useEffect(() => {
    if (aiSolutionData?.vault !== undefined && selectedSolutionFromLibrary.id) {
      setSelectedSolutionFromLibrary((prev) => ({
        ...prev,
        vault: aiSolutionData.vault,
      }));
    }
  }, [aiSolutionData?.vault, selectedSolutionFromLibrary.id]);

  const [, setSolutionsLoading] = useState(false);
  const [dataSourceType, setDataSourceType] = useState<'rest' | 'graphql' | null>(null);

  // Track diagram edits for custom processType
  const [, setIsDiagramEdited] = useState(false);

  const selectedOrgId = localStorage.getItem('current_organization');

  const fetchAISolution = async (): Promise<any> => {
    try {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramData?.diagram.id}`;

      const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);

      if (!response) throw new Error('Invalid response structure');
      return response;
    } catch (err) {
      console.error('Error fetching solution by ID (REST):', err);
      return null;
    }
  };

  const {
    data,
    error: solutionsError,
    loading: qlLoading,
  } = useGraphQLQuery(
    GET_AI_SOLUTIONS_DATA,
    {
      v1: { subprocessId: `${selectedCard?.id}` },
      v2: ['name'],
    },
    { skip: !activeModal || activeModal !== 'aiSolution' }
  );

  useEffect(() => {
    const loadSolutions = async () => {
      setSolutionsLoading(true);

      try {
        const response = await fetchAISolution();
        if (response?.results?.length > 0) {
          setSolutionData(
            response.results.map(
              ({
                id,
                opportunity_name,
                impact,
                vault,
              }: {
                id: string;
                opportunity_name: string;
                impact?: string;
                vault?: boolean;
              }) => ({
                id,
                opportunity_name,
                impact: impact || null,
                vault: vault || false,
              })
            )
          );
          setDataSourceType('rest');
          return;
        }
        if (qlLoading) {
          setSolutionsLoading(true);
        }
        if (data?.aiSolution?.fetch?.length) {
          setSolutionData(
            data.aiSolution.fetch.map(
              ({
                id,
                name,
                impactAssessment,
              }: {
                id: string;
                name: string;
                impactAssessment?: { general: string };
              }) => ({
                id,
                opportunity_name: name,
                impact: impactAssessment?.general ?? 'N/A',
              })
            )
          );
          setDataSourceType('graphql');
        } else if (!qlLoading) {
          setSolutionData([]);
          setDataSourceType(null);
        }
      } catch (err) {
        console.error('Error loading solutions:', err);
        ShowToast({ message: 'Cannot Load Solutions', type: ToastTypeEnum.Error });
      } finally {
        setSolutionsLoading(false);
      }
    };

    if (activeModal === 'aiSolution') {
      loadSolutions();
    }
  }, [activeModal, data, solutionsError, setSolutionData]);

  // const handleAiSolutionsClick = () => {
  //   setAiSolutionToggle((prev) => !prev);
  // };

  // const handleViewSolution = (data: { name: string; id: string; impact: string }) => {
  //   setSelectedSolutionFromLibrary({ id: data.id, name: data.name });
  // };

  // const handleCloseAiSolutions = () => setAiSolutionToggle(false);
  const dispatch = useDispatch();

  const { data: aiEnablerData } = useGraphQLQuery<{ aiEnabler: { fetch: Technology[] } }>(
    GET_AI_ENABLER_DATA,
    {
      v1: {},
      v2: ['name'],
    }
  );

  const defaultResetAll = () => window.history.back();
  const defaultResetTaxonomy = () => window.history.back();
  const defaultResetE2E = () => window.history.back();

  const actualOnResetAll = onResetAll || defaultResetAll;
  const actualOnResetTaxonomy = onResetTaxonomy || defaultResetTaxonomy;
  const actualOnResetE2E = onResetE2E || defaultResetE2E;
  const actualOnResetSolution = onResetSolution || defaultResetAll;

  const handleSaveDiagram = async () => {
    if (!diagramData || !diagramData.diagram || !diagramData.diagram.id) {
      setSaveError('No diagram data available to save.');
      return;
    }

    try {
      setSavingDiagram(true);
      setSaveError(null);

      let xmlToSave = diagramData.diagram.xml;
      let dataTosave = diagramData.diagram.data || {};

      if (bpmnModelerRef.current && isEditPermission) {
        const result = await bpmnModelerRef.current.saveXML({ format: true });
        if (result?.xml) {
          xmlToSave = result.xml;
        }
      }

      console.log('Saving diagram with XML:', xmlToSave);

      const formData = new FormData();
      formData.append('xmlData', new File([xmlToSave], 'diagram.xml', { type: 'text/xml' }));
      formData.append('data', JSON.stringify(dataTosave));

      await saveDiagram(diagramData.diagram.id, formData);

      if (location?.pathname?.includes('ideation-xplr')) {
        lastSavedXmlRef.current = xmlToSave;
        setPrevDiagramData(dataTosave);
      }

      dispatch(
        updateDiagramData({
          DiagramData: {
            ...diagramData,
            diagram: {
              ...diagramData.diagram,
              xml: xmlToSave,
              data: dataTosave,
            },
          },
        })
      );

      setSaveSuccess(true);
      ShowToast({ message: 'BPMN Diagram saved successfully', type: ToastTypeEnum.Success });
      setTimeout(() => setSaveSuccess(false), 3000);

      // For custom process types - mark as saved after diagram edit
      if (selectedSubprocess?.processType === 'custom' || selectedCard?.processType === 'custom') {
        setIsSaved(true);
        setIsDiagramChanged(false);
        setIsDiagramEdited(true);

        // CRITICAL: Pass new XML and data to parent to update Redux
        if (onDiagramSaved) {
          onDiagramSaved(xmlToSave, dataTosave);
        }
      } else if (isProcessDiagram) {
        // For process diagrams, reset change state but don't close
        setIsDiagramChanged(false);
        setIsSaved(true);
        setIsDiagramEdited(false);
        // Notify parent that changes have been saved
        if (onDiagramChange) {
          onDiagramChange(false);
        }
        // Pass new XML and data to parent to update Redux
        if (onDiagramSaved) {
          onDiagramSaved(xmlToSave, dataTosave);
        }
        // CRITICAL FIX: Reload the diagram viewer with the saved XML to reflect changes in UI
        if (bpmnModelerRef.current && containerRef.current) {
          try {
            // Destroy existing modeler/viewer
            bpmnModelerRef.current.destroy();
            bpmnModelerRef.current = null;

            // Reinitialize with the saved XML
            bpmnModelerRef.current = isEditPermission
              ? initializeBpmnModeler(containerRef.current)
              : initializeBpmnViewer(containerRef.current);

            // Import the updated XML
            await bpmnModelerRef.current.importXML(xmlToSave);

            // Re-attach event listeners
            attachEventListeners();

            // Update diagram badges if needed
            setTimeout(() => {
              updateDiagramBadges();
            }, 0);

            console.log('Diagram reloaded with saved changes');
          } catch (err) {
            console.error('Failed to reload diagram after save:', err);
            setSaveError('Failed to reload diagram with saved changes');
          }
        }
      } else {
        // For non-custom process types, reset all states and close
        setIsDiagramChanged(false);
        setIsSaved(false);
        setIsDiagramEdited(false);
        dispatch(closeDiagram());
      }
    } catch (error: any) {
      console.error('Failed to save diagram:', error);
      setSaveError(error.message || 'Failed to save diagram. Please try again.');
    } finally {
      setSavingDiagram(false);
    }
  };

  const handleZoomIn = () => {
    if (bpmnModelerRef.current && containerRef.current) {
      const canvas = bpmnModelerRef.current.get<Canvas>('canvas');
      const rect = containerRef.current.getBoundingClientRect();
      const center = {
        x: rect.width / 2,
        y: rect.height / 2,
      };
      const newScale = Math.min(currentScale + 0.1, 2);
      canvas.zoom(newScale, center);
      setCurrentScale(newScale);
    }
  };

  const handleZoomOut = () => {
    if (bpmnModelerRef.current && containerRef.current) {
      const canvas = bpmnModelerRef.current.get<Canvas>('canvas');
      const rect = containerRef.current.getBoundingClientRect();
      const center = {
        x: rect.width / 2,
        y: rect.height / 2,
      };
      const newScale = Math.max(currentScale - 0.1, 0.2);
      canvas.zoom(newScale, center);
      setCurrentScale(newScale);
    }
  };

  const handleDiscardDiagramChanges = useCallback(async () => {
    try {
      if (location?.pathname?.includes('ideation-xplr')) {
        const previousXml = lastSavedXmlRef.current || diagramData?.diagram?.xml || '';
        const previousData = prevDiagramData || diagramData?.diagram?.data || {};

        if (!previousXml || !containerRef.current) {
          return;
        }
        if (bpmnModelerRef.current) {
          try {
            bpmnModelerRef.current.destroy();
          } catch {}
          bpmnModelerRef.current = null as any;
        }

        if (bpmnModelerRef.current) {
          try {
            bpmnModelerRef.current.destroy();
          } catch {}
          bpmnModelerRef.current = null as any;
        }

        bpmnModelerRef.current = isEditPermission
          ? initializeBpmnModeler(containerRef.current)
          : initializeBpmnViewer(containerRef.current);

        await bpmnModelerRef.current.importXML(previousXml);

        dispatch(
          updateDiagramData({
            DiagramData: {
              diagram: {
                id: diagramData?.diagram?.id,
                xml: previousXml,
                data: previousData,
              },
            },
          })
        );

        attachEventListeners();
        setTimeout(() => {
          updateDiagramBadges();
        }, 0);

        setIsDiagramChanged(false);
        setIsSaved(false);
        setIsDiagramEdited(false);

        if (onDiagramChange && isProcessDiagram) {
          onDiagramChange(false);
        }

        ShowToast({ message: 'Changes discarded', type: ToastTypeEnum.Success });
      } else {
        const previousXml = diagramData?.diagram?.xml || '';
        if (!previousXml || !containerRef.current) {
          return;
        }

        if (bpmnModelerRef.current) {
          try {
            bpmnModelerRef.current.destroy();
          } catch {}
          bpmnModelerRef.current = null as any;
        }

        bpmnModelerRef.current = isEditPermission
          ? initializeBpmnModeler(containerRef.current)
          : initializeBpmnViewer(containerRef.current);

        await bpmnModelerRef.current.importXML(previousXml);

        attachEventListeners();
        setTimeout(() => {
          updateDiagramBadges();
        }, 0);

        setIsDiagramChanged(false);
        setIsSaved(false);
        setIsDiagramEdited(false);

        if (onDiagramChange && isProcessDiagram) {
          onDiagramChange(false);
        }
      }
    } catch (err) {
      console.error('Failed to discard changes and reload previous diagram:', err);
      ShowToast({ message: 'Failed to discard changes', type: ToastTypeEnum.Error });
    }
  }, [
    isEditPermission,
    onDiagramChange,
    isProcessDiagram,
    prevDiagramData,
    diagramData,
    location?.pathname,
  ]);
  const handleClose = useCallback(() => {
    if (isProcessDiagram && isDiagramChanged && shouldShowSaveButtons()) {
      setShowUnsavedChangesModal(true);
      return;
    }

    if (onClose) {
      onClose();
      return;
    }
    dispatch(closeDiagram());
  }, [dispatch, onClose, isProcessDiagram, isDiagramChanged]);

  const handleAddToSolutionXPLR = useCallback(() => {
    handleClose();
    navigate(`/solution-xplr?subprocessId=${selectedCard?.id ?? ''}`);
  }, [handleClose, navigate, selectedCard?.id]);

  const handleInitialFitView = () => {
    if (bpmnModelerRef.current) {
      const canvas = bpmnModelerRef.current.get<Canvas>('canvas');
      // Simply fit to viewport - this will show entire diagram on screen
      canvas.zoom('fit-viewport' as any, 'auto' as any);
      setCurrentScale(1);
    }
  };
  const centerDiagram = () => {
    if (!bpmnModelerRef.current || !containerRef.current) return;

    const canvas = bpmnModelerRef.current.get('canvas') as Canvas;
    const container = containerRef.current;

    const elementRegistry = bpmnModelerRef.current.get('elementRegistry') as ElementRegistry;
    const definitions = elementRegistry.getAll();

    if (definitions.length === 0) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    definitions.forEach((element: any) => {
      if (element.x !== undefined && element.y !== undefined) {
        minX = Math.min(minX, element.x);
        minY = Math.min(minY, element.y);
        maxX = Math.max(maxX, element.x + (element.width || 0));
        maxY = Math.max(maxY, element.y + (element.height || 0));
      }
    });

    const padding = 50;
    const diagramWidth = maxX - minX + padding * 2;
    const diagramHeight = maxY - minY + padding * 2;
    const diagramCenterX = minX + (maxX - minX) / 2;
    const diagramCenterY = minY + (maxY - minY) / 2;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const scaleX = containerWidth / diagramWidth;
    const scaleY = containerHeight / diagramHeight;
    const scale = Math.min(scaleX, scaleY) * 0.55;

    canvas.zoom(scale);

    const viewboxWidth = containerWidth / scale;
    const viewboxHeight = containerHeight / scale;

    canvas.viewbox({
      x: diagramCenterX - viewboxWidth / 2,
      y: diagramCenterY - viewboxHeight / 2,
      width: viewboxWidth,
      height: viewboxHeight,
    });

    setCurrentScale(scale);
  };

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (bpmnModelerRef.current) {
      const timer = setTimeout(() => {
        try {
          const canvas = bpmnModelerRef.current?.get<Canvas>('canvas');
          if (canvas) {
            canvas.resized();

            if (isExpanded) {
              // When expanding, center the diagram
              centerDiagram();
            } else {
              handleInitialFitView();
            }
          }
        } catch (error) {
          console.error('Error resizing diagram:', error);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isExpanded]);
  const handleFitViewToggle = () => {
    if (bpmnModelerRef.current) {
      const canvas = bpmnModelerRef.current.get<Canvas>('canvas');

      if (!isFitViewToggled) {
        // Going to fit view
        canvas.zoom('fit-viewport' as any, 'auto' as any);
        setCurrentScale(1);
        setIsFitViewToggled(true);
      } else {
        // Reset to initial fit view
        canvas.zoom('fit-viewport' as any, 'auto' as any);
        setCurrentScale(1);
        setIsFitViewToggled(false);
      }
    }
  };
  const handleElementHover = (event: any) => {
    const element = event.element;
    const allowedTypes = [
      'bpmn:Task',
      'bpmn:UserTask',
      'bpmn:ServiceTask',
      'bpmn:SendTask',
      'bpmn:ReceiveTask',
      'bpmn:ManualTask',
      'bpmn:BusinessRuleTask',
      'bpmn:ScriptTask',
      'bpmn:Activity',
      'bpmn:SubProcess',
    ];

    if (allowedTypes.includes(element.type)) {
      const elementRegistry = bpmnModelerRef.current?.get<ElementRegistry>('elementRegistry');
      const gfx = elementRegistry?.getGraphics(element);

      if (!gfx) {
        console.warn('No graphics found for element');
        return;
      }
      setHoveredElements((prev) => new Set(prev).add(element.id));
    }
  };

  const handleElementOut = (event: any) => {
    if (!showAllAiComponents) {
      setHoveredElements((prev) => {
        const newSet = new Set(prev);
        newSet.delete(event.element.id);
        return newSet;
      });
    }
  };

  const handleToggleAiSteps = () => {
    setShowAllAiComponents((prev) => !prev);
    if (showAllAiComponents) {
      setHoveredElements(new Set());
    } else {
      const elementRegistry = bpmnModelerRef.current?.get<ElementRegistry>('elementRegistry');
      if (elementRegistry) {
        const allElements = elementRegistry.getAll();
        const allowedTypes = [
          'bpmn:Task',
          'bpmn:UserTask',
          'bpmn:ServiceTask',
          'bpmn:SendTask',
          'bpmn:ReceiveTask',
          'bpmn:ManualTask',
          'bpmn:BusinessRuleTask',
          'bpmn:ScriptTask',
          'bpmn:Activity',
          'bpmn:SubProcess',
        ];

        const newHoveredElements = new Set<string>();
        allElements.forEach((element) => {
          if (allowedTypes.includes(element.type)) {
            newHoveredElements.add(element.id);
          }
        });
        setHoveredElements(newHoveredElements);
      }
    }
  };

  const handleElementClick = async (event: any) => {
    const element = event.element;
    const allowedTypes = [
      'bpmn:Task',
      'bpmn:UserTask',
      'bpmn:ServiceTask',
      'bpmn:SendTask',
      'bpmn:ReceiveTask',
      'bpmn:ManualTask',
      'bpmn:BusinessRuleTask',
      'bpmn:ScriptTask',
      'bpmn:Activity',
      'bpmn:SubProcess',
    ];

    if (allowedTypes.includes(element.type) && diagramData) {
      const elementDetails = diagramData.diagram.data[element.id];

      if (elementDetails) {
        try {
          const technologies = aiEnablerData?.aiEnabler?.fetch || [];
          const technologyNames = technologies.map((tech) => tech.name);
          const relatedOpportunities = aiOpportunityData.filter(
            (opp) => opp.shape_id === element.id
          );

          setModal({
            isOpen: true,
            activeTab: 'aiHubble',
            elementData: {
              id: element.id,
              type: element.type,
              name: elementDetails.name,
              description: elementDetails.description,
              aiEnablers: technologyNames,
            },
            opportunities: relatedOpportunities,
          });
          setActiveModal('diagram');
        } catch (error) {
          console.error('Error fetching technology names:', error);
          setError('Failed to load AI technologies');
        }
      } else {
        console.warn(`No additional data found for element ID: ${element.id}`);
        setModal({
          isOpen: true,
          activeTab: 'aiHubble',
          elementData: {
            id: element.id,
            type: element.type,
            name: element.businessObject.name || 'Unnamed Element',
            description: 'No detailed description available.',
            aiEnablers: [],
          },
          opportunities: [],
        });
        setActiveModal('diagram');
      }
    }
  };

  const handleModalClose = () => {
    setModal({
      isOpen: false,
      elementData: null,
      activeTab: 'aiHubble',
      opportunities: [],
    });
    setActiveModal(null);
  };

  const handleDeleteProcess = async () => {
    try {
      const response: ApiResponse = await httpService(
        httpConstants.METHOD_TYPE.DELETE,
        `${API_GRAPH_BASE}/custom-process/organization/${selectedOrgId}/custom-taxonomy/${selectedCard?.id}`
      );

      if (response && (response.success === true || response.status === 200 || !response.error)) {
        dispatch(closeDiagram());
        dispatch(refreshTaxonomy(true));

        setShowDeleteModal(false);
        window.location.reload();

        console.log('Data source deleted successfully');
        return true;
      } else {
        console.error('DELETE API Error:', response?.error || 'Unknown error');
        return false;
      }
    } catch (error) {
      console.error('Failed to delete data source:', error);
      return false;
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDeleteIdeation = async () => {
    try {
      const caseIdToUse = selectedCardUseCaseId || useCaseId;

      if (!caseIdToUse) {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      const url = `${API_BASE}/api/v1/ideation/use-cases/${caseIdToUse}/delete/`;
      const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url);

      if (response) {
        ShowToast({ message: 'Ideation deleted successfully', type: ToastTypeEnum.Success });
        setShowDeleteIdeationModal(false);

        // Navigate to ideation dashboard - close the diagram
        handleClose();
      } else {
        ShowToast({ message: 'Failed to delete ideation', type: ToastTypeEnum.Error });
      }
    } catch (error) {
      console.error('Error deleting ideation:', error);
      ShowToast({ message: 'Error deleting ideation', type: ToastTypeEnum.Error });
      setShowDeleteIdeationModal(false);
    }
  };

  const handleCloseDeleteIdeationModal = () => {
    setShowDeleteIdeationModal(false);
  };

  const handleUpdateRenameProcess = async (newName: string) => {
    try {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/level2/${selectedCard?.id}/`;
      const payload = { name: newName };

      await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);

      setRenameProcessName(newName);
      // Update ideation slice (form state) and also update the diagram viewer
      // so the UI that reads `selectedCard.name` is updated immediately.
      dispatch(updateIdeationFieldState({ field: 'selectedProcess', value: newName }));
      dispatch(
        updateDiagramData({
          selectedCard: {
            ...selectedCard,
            name: newName,
          },
        })
      );
      setShowRenameModal(false);
      dispatch(refreshTaxonomy(true));
      ShowToast({ message: 'Process name updated successfully', type: ToastTypeEnum.Success });
    } catch (error) {
      console.error('Error updating process name:', error);
      ShowToast({ message: 'Failed to update process name', type: ToastTypeEnum.Error });
    }
  };

  // const addBadge = (element: any, overlays: any, count: number) => {
  //   const htmlContent = document.createElement('div');
  //   htmlContent.innerHTML = `
  //     <div style="width: 20px; height: 20px; background-color: #CC3333; border-radius: 50%; position: relative; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; font-weight: bold; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 1000;">
  //       ${count}
  //     </div>
  //   `;

  //   overlays.add(element, {
  //     position: { top: -4, right: 10 },
  //     html: htmlContent,
  //   });
  // };

  const updateDiagramBadges = useCallback(() => {
    if (!bpmnModelerRef.current) return;

    try {
      const overlays = bpmnModelerRef.current.get('overlays') as any;
      const elementRegistry = bpmnModelerRef.current.get<ElementRegistry>('elementRegistry');

      if (!overlays || !elementRegistry) return;

      const aiOpportunities = (window._aiOpportunityData ||
        aiOpportunityData ||
        []) as Opportunity[];

      const shapeIdCountMap = new Map<string, number>();

      aiOpportunities.forEach((opp: Opportunity) => {
        if (opp.valid === true) {
          shapeIdCountMap.set(opp.shape_id, (shapeIdCountMap.get(opp.shape_id) || 0) + 1);
        } else {
          shapeIdCountMap.set(opp.shape_id, shapeIdCountMap.get(opp.shape_id) || 0);
        }
      });

      // shapeIdCountMap.forEach((count, shapeId) => {
      //   const element = elementRegistry.get(shapeId);
      //   if (element && count > 0) {
      //     addBadge(element, overlays, count);
      //   } else if (element && count === 0) {
      //     if (typeof overlays.remove === 'function') {
      //       overlays.remove({ element });
      //     }
      //   }
      // });
    } catch (error) {
      console.error('Error updating diagram badges:', error);
    }
  }, [aiOpportunityData]);

  useEffect(() => {
    if (bpmnModelerRef.current && (aiOpportunityData.length > 0 || window._aiOpportunityData)) {
      setTimeout(() => {
        updateDiagramBadges();
      }, 0);
    }
  }, [aiOpportunityData, updateDiagramBadges]);

  const handleOpportunityAdd = async (
    opportunity: Opportunity & { allOpportunities?: Opportunity[] }
  ) => {
    let updatedOpportunities: Opportunity[];

    if (opportunity.allOpportunities) {
      updatedOpportunities = opportunity.allOpportunities;
    } else {
      const currentOpportunities = (window._aiOpportunityData ||
        aiOpportunityData ||
        []) as Opportunity[];
      updatedOpportunities = [...currentOpportunities];
      const index = updatedOpportunities.findIndex((opp) => opp.id === opportunity.id);

      if (index >= 0) {
        updatedOpportunities[index] = opportunity;
      } else {
        updatedOpportunities.push(opportunity);
      }
    }

    window._aiOpportunityData = updatedOpportunities;

    updateDiagramBadges();
    dispatch(setOpportunities(updatedOpportunities));
    const customEvent = new CustomEvent('aiOpportunityUpdated', {
      detail: { opportunities: updatedOpportunities },
    });
    window.dispatchEvent(customEvent);

    setOpportunityUpdated(true);
    setTimeout(() => {
      setOpportunityUpdated(false);
    }, 3000);
  };

  const initializeBpmnModeler = (container: HTMLElement, isCompact = false) => {
    return new BpmnModeler({
      container,
      height: '100%',
      width: '100%',
      position: isProcessDiagram ? 'relative' : 'absolute',
      propertiesPanel: {
        parent: '#properties-panel',
      },
      palette: {
        open: true,
      },
      contextPad: {
        open: true,
      },

      textRenderer: {
        defaultStyle: {
          fontSize: '10px',
          lineHeight: '1.7',
        },
        layout: {
          preRender: true,
          wrap: true,
          maxChar: 50,
        },
        defaultSize: {
          width: isCompact ? 450 : 500,
          maxWidth: isCompact ? 400 : 450,
          height: isCompact ? 400 : 500,
        },
      },

      zoomScroll: {
        enabled: true,
        pinchZoom: true,
        scrollZoom: true,
        zoomSpeed: 0.1,
        pinchZoomEnabled: true,
        minimumZoom: 0.2,
        maximumZoom: 4,
      },
      moveCanvas: {
        enabled: true,
      },
      canvas: {
        deferUpdate: false,
      },
    });
  };

  const initializeBpmnViewer = (container: HTMLElement, isCompact = false) => {
    return new BpmnJS({
      container,
      height: '100%',
      width: '100%',
      position: isProcessDiagram ? 'relative' : 'absolute',

      textRenderer: {
        defaultStyle: {
          fontSize: '10px',
          lineHeight: '1.7',
        },
        layout: {
          preRender: true,
          wrap: true,
          maxChar: 50,
        },
        defaultSize: {
          width: isCompact ? 450 : 500,
          maxWidth: isCompact ? 400 : 450,
          height: isCompact ? 400 : 500,
        },
      },

      zoomScroll: {
        enabled: true,
        pinchZoom: true,
        scrollZoom: false,
      },
      moveCanvas: {
        enabled: true,
      },
    });
  };

  // Track diagram changes for custom processType and process diagrams
  const handleDiagramChange = useCallback(() => {
    if (isProcessDiagram && isEditPermission) {
      setIsDiagramChanged(true);
      setIsSaved(false); // Reset saved state when diagram changes
      setIsDiagramEdited(true);
    } else if (
      (selectedSubprocess?.processType === 'custom' || selectedCard?.processType === 'custom') &&
      isEditPermission
    ) {
      setIsDiagramChanged(true);
      setIsSaved(false); // Reset saved state when diagram changes
      setIsDiagramEdited(true);
    }
  }, [
    isProcessDiagram,
    selectedSubprocess?.processType,
    selectedCard?.processType,
    isEditPermission,
  ]);

  // Notify parent when diagram changes
  useEffect(() => {
    if (onDiagramChange && isProcessDiagram) {
      onDiagramChange(isDiagramChanged);
    }
  }, [isDiagramChanged, isProcessDiagram, onDiagramChange]);

  // Handle step description update from DiagramModal
  const handleStepDescriptionUpdate = useCallback(() => {
    if (location?.pathname?.includes('ideation-xplr')) {
      setIsDiagramChanged(true);
      setIsSaved(false);
      setIsDiagramEdited(true);

      if (onDiagramChange) {
        onDiagramChange(true);
      }
    }

    if (
      (selectedSubprocess?.processType === 'custom' || selectedCard?.processType === 'custom') &&
      isEditPermission
    ) {
      setIsStepDescriptionUpdated(true);
      setIsDiagramEdited(true);
    }
  }, [
    selectedSubprocess?.processType,
    selectedCard?.processType,
    isEditPermission,
    onDiagramChange,
    location?.pathname,
  ]);

  // Function to attach event listeners
  const attachEventListeners = useCallback(() => {
    if (bpmnModelerRef.current) {
      const eventBus = bpmnModelerRef.current.get('eventBus') as EventBus;

      // Remove existing listeners first to avoid duplicates
      eventBus.off('element.hover', handleElementHover);
      eventBus.off('element.out', handleElementOut);
      eventBus.off('element.click', handleElementClick);
      eventBus.off('commandStack.changed', handleDiagramChange);

      // Attach new listeners
      eventBus.on('element.hover', handleElementHover);
      eventBus.on('element.out', handleElementOut);
      eventBus.on('element.click', handleElementClick);

      // Track diagram changes for custom processType
      if (
        (selectedSubprocess?.processType === 'custom' ||
          selectedCard?.processType === 'custom' ||
          isProcessDiagram) &&
        isEditPermission
      ) {
        eventBus.on('commandStack.changed', handleDiagramChange);
      }
    }
  }, [
    diagramData,
    aiOpportunityData.length,
    isEditPermission,
    handleDiagramChange,
    selectedSubprocess?.processType,
    selectedCard?.processType,
  ]);

  useEffect(() => {
    // Initialize the window property with current aiOpportunityData
    window._aiOpportunityData = [...aiOpportunityData];

    if (!diagramData || !diagramData.diagram || !diagramData.diagram.xml) {
      // setLoading(false); // Optionally set loading to false if data is not present
      // setError('No diagram data or XML provided.'); // Optionally set error
      return;
    }

    const xmlContent = diagramData.diagram.xml;

    setLoading(true);
    setError(null);

    if (bpmnModelerRef.current) {
      bpmnModelerRef.current.destroy();
      bpmnModelerRef.current = null;
    }

    if (containerRef.current) {
      bpmnModelerRef.current = isEditPermission
        ? initializeBpmnModeler(containerRef.current)
        : initializeBpmnViewer(containerRef.current);

      (window as any).bpmnModelerInstance = bpmnModelerRef.current;
      lastSavedXmlRef.current = xmlContent;
      if (!prevDiagramData || !Object.keys(prevDiagramData)?.length)
        setPrevDiagramData(diagramData?.diagram?.data);

      // Import the XML directly
      bpmnModelerRef.current
        .importXML(xmlContent)
        .then(({ warnings }) => {
          if (warnings?.length) {
            console.warn('BPMN import warnings:', warnings);
          }
          handleInitialFitView();

          attachEventListeners();

          setTimeout(() => {
            updateDiagramBadges();
          }, 0);

          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to render BPMN diagram:', err);
          // setError('Failed to load or render diagram: ' + err.message);
          setLoading(false);
        });
    }

    return () => {
      if (bpmnModelerRef.current) {
        const eventBus = bpmnModelerRef.current.get('eventBus') as EventBus;
        eventBus.off('element.hover', handleElementHover);
        eventBus.off('element.out', handleElementOut);
        eventBus.off('element.click', handleElementClick);
        eventBus.off('commandStack.changed', handleDiagramChange);
        bpmnModelerRef.current.destroy();
        bpmnModelerRef.current = null;
        delete (window as any).bpmnModelerInstance;
      }
    };
  }, [
    diagramData,
    aiOpportunityData.length,
    isEditPermission,
    attachEventListeners,
    updateDiagramBadges,
  ]);

  const handleApplyEnterpriseComplete = () => {
    setTimeout(() => {
      attachEventListeners();

      updateDiagramBadges();
    }, 200);
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleMouseWheel = (event: WheelEvent) => {
    event.preventDefault();
    if (bpmnModelerRef.current) {
      const canvas = bpmnModelerRef.current.get<Canvas>('canvas');
      const delta = event.deltaY > 0 ? -0.05 : 0.05;
      const newScale = Math.min(Math.max(currentScale + delta, 0.2), 4);

      const rect = containerRef.current?.getBoundingClientRect();
      const mousePosition = {
        x: event.clientX - (rect?.left || 0),
        y: event.clientY - (rect?.top || 0),
      };

      canvas.zoom(newScale, mousePosition);
      setCurrentScale(newScale);

      if (showAllAiComponents) {
        setShowAllAiComponents(false);
        setTimeout(() => setShowAllAiComponents(true), 0);
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleMouseWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleMouseWheel);
      };
    }
  }, [currentScale, showAllAiComponents]);

  // const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleVaultChange = async (id: string, state: boolean) => {
    const url = `${API_BASE}/api/v1/e2e_taxonomy/new-vault/`;
    try {
      const response = await httpService<any>(
        state ? httpConstants.METHOD_TYPE.POST : httpConstants.METHOD_TYPE.DELETE,
        url,
        {
          organization_id: currentOrg,
          ai_solution_id: id,
        }
      );
      return !!response; // true if response is not null/undefined
    } catch (error) {
      console.error('Vault update failed:', error);
      return false;
    }
  };

  const handleToggleVault = async () => {
    if (!selectedSolutionFromLibrary || !selectedSolutionFromLibrary.id) return;

    const newState = !selectedSolutionFromLibrary.vault;
    const success = await handleVaultChange(selectedSolutionFromLibrary.id, newState);

    if (success) {
      setSelectedSolutionFromLibrary({ ...selectedSolutionFromLibrary, vault: newState });
      // Sync aiSolutionData vault status
      setAiSolutionData((prev) => (prev ? { ...prev, vault: newState } : null));
      if (newState) {
        ShowToast({ message: 'Solution added to list', type: ToastTypeEnum.Success });
      } else {
        ShowToast({ message: 'Solution removed from list', type: ToastTypeEnum.Success });
      }
    } else {
      ShowToast({ message: 'Unable to process request', type: ToastTypeEnum.Error });
    }
    setShowVaultModal(false);
  };

  const handleUpdateKPIs = async (process_description: string, kpis: any) => {
    const url = `${API_GRAPH_BASE}/custom-process/subprocess/${selectedCard?.id}`;
    try {
      const response = await httpService<any>(httpConstants.METHOD_TYPE.PUT, url, {
        process_description: process_description,
        kpis: kpis,
      });

      return !!response;
    } catch (error) {
      console.error('KPI update failed:', error);
      return false;
    }
  };

  const processReportData = async () => {
    try {
      const selectedCardUseCaseId =
        (selectedCard as any)?.useCaseId ||
        (selectedCard as any)?.use_case_id ||
        (selectedCard as any)?.id;
      const caseIdToUse = selectedCardUseCaseId || useCaseId;

      if (!caseIdToUse) {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      const ideationDetails = await httpService<any>(
        httpConstants.METHOD_TYPE.GET,
        `${API_BASE}/api/v1/ideation/use-cases/${caseIdToUse}/`
      );

      const proposedEnhancementsFromApiRaw = ideationDetails?.proposed_enhancements?.items ?? [];

      const proposedEnhancements: Enhancement[] = Array.isArray(proposedEnhancementsFromApiRaw)
        ? proposedEnhancementsFromApiRaw.map((item: any, index: number) => ({
            id: item.id ?? String(index),
            enhancement_title: item.opportunity_title || item.title || 'AI Enhancement Opportunity',
            enhancement_description:
              item.opportunity_description || item.description || 'Description not available',
          }))
        : [];

      // Get diagram ID from diagramData
      const diagramId =
        (diagramData as any)?.diagram?.id || (diagramData as any)?.DiagramData?.diagram?.id || '';

      // Fetch detailed solution data for all AI opportunities using diagram_id
      const opportunitiesIDsResponse = await httpService<{ results: any[] }>(
        httpConstants.METHOD_TYPE.GET,
        `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramId}`
      );

      const opportunitiesIDs = opportunitiesIDsResponse?.results.map((item: any) => item.id) ?? [];

      const completeSolutionData = await Promise.all(
        opportunitiesIDs.map(
          async (id: string) =>
            await fetchAISolutionWithAgentEnablement({
              solutionId: String(id),
              diagramId: diagramId,
              organizationId: currentOrg,
            })
        )
      );

      const validSolutionData = completeSolutionData.filter(
        (item): item is NonNullable<(typeof completeSolutionData)[number]> => item != null
      );

      validSolutionData.forEach((item: any) => {
        const custom = item?.agent?.ai_enablement?.enablement_matrix?.custom ?? [];
        const embedded = item?.agent?.ai_enablement?.enablement_matrix?.embedded ?? [];
        const extended = item?.agent?.ai_enablement?.enablement_matrix?.extended ?? [];

        item.agent_data = {
          custom,
          embedded,
          extended,
        };
      });

      // Get diagram XML from diagramData
      const diagramXML =
        (diagramData as any)?.diagram?.xml || (diagramData as any)?.DiagramData?.diagram?.xml || '';

      let diagram_image_base64 = '';
      if (diagramXML) {
        try {
          diagram_image_base64 = await convertBpmnXmlToBase64(diagramXML, 8);
        } catch (conversionError) {
          console.error('Failed to convert BPMN XML to base64 image:', conversionError);
        }
      }

      // Build a deep-link URL to the live diagram page so the report can contain
      const processId = (selectedCard as any)?.id ?? '';
      let diagramUrl = '';
      if (diagramId) {
        const origin = window.location.origin;
        const query = [
          `diagramId=${encodeURIComponent(diagramId)}`,
          ...(processId ? [`processId=${encodeURIComponent(processId)}`] : []),
          ...(currentOrg && currentOrg !== 'Empty'
            ? [`organizationId=${encodeURIComponent(currentOrg)}`]
            : []),
        ].join('&');
        diagramUrl = `${origin}/diagram?${query}`;
      }

      let modifiedSubTitle =
        (diagramData as any)?.useCaseData?.Opportunity_name ||
        (selectedCard as any)?.name ||
        'Detailed Opportunity Report';

      const opportunityName = (diagramData as any)?.useCaseData?.Opportunity_name;
      if (typeof opportunityName === 'string') {
        const regex = /^([A-Za-z]{3}-\d+):\s*(.*)$/;
        const match = opportunityName.match(regex);
        if (match && match[2]) {
          modifiedSubTitle = match[2];
          if (modifiedSubTitle.length > 0) {
            modifiedSubTitle = modifiedSubTitle[0].toUpperCase() + modifiedSubTitle.slice(1);
          }
        }
      }

      const payload: Record<string, unknown> = {
        subtitle: modifiedSubTitle,
        proposedEnhancements,
        validSolutionData,
        diagramXML: diagram_image_base64,
        diagramUrl,
        link: {
          text: 'View Diagram',
          url: diagramUrl,
        },
      };

      return payload;
    } catch (error) {
      console.error('Error processing report data:', error);
      throw error;
    }
  };

  const handleDownloadReport = async () => {
    setLoadingDocument(true);
    setDownloadProgressOpen(true);
    setDownloadProgress(0);
    try {
      const templateUrl = '/docx/ideation-report-new.docx';

      // Get processed data
      setDownloadProgress(30);
      const basePayload = await processReportData();

      if (!basePayload) {
        throw new Error('Failed to process report data');
      }

      setDownloadProgress(60);
      let wordBlob = await generateWordWithAccurateTOC(templateUrl, basePayload);

      if (!wordBlob) {
        throw new Error('Failed to generate Word document');
      }

      // CONDITIONAL: Only update links if diagramUrl exists
      if (basePayload.diagramUrl && typeof basePayload.diagramUrl === 'string') {
        wordBlob = await updateWordDocLinks(wordBlob, [
          {
            oldUrl: REPORT_PROCESS_DIAGRAM_REPLACABLE_LINK,
            newUrl: basePayload.diagramUrl,
          },
        ]);
      }

      setDownloadProgress(90);
      // Download the Word document
      const url = window.URL.createObjectURL(wordBlob);
      const link = document.createElement('a');
      link.href = url;
      const fileName =
        (diagramData as any)?.useCaseData?.Opportunity_name ||
        (selectedCard as any)?.name ||
        'Detailed Opportunity Report';
      link.setAttribute('download', `${fileName}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadProgress(100);
      // Close the snackbar after a short delay
      setTimeout(() => {
        setDownloadProgressOpen(false);
        setDownloadProgress(0);
      }, 1500);
    } catch (err) {
      console.error('Word document download failed', err);
      setDownloadProgressOpen(false);
      setDownloadProgress(0);
      ShowToast({ message: 'Failed to download report', type: ToastTypeEnum.Error });
    } finally {
      setLoadingDocument(false);
    }
  };

  // Function to check status
  // const checkStatus = useCallback(async (id: string): Promise<string> => {
  //   try {
  //     const res = await httpService<{
  //       id: string;
  //       name: string;
  //       status: string;
  //       diagram: Record<string, any>;
  //       aisolution: Record<string, any>;
  //     }>(
  //       httpConstants.METHOD_TYPE.GET,
  //       `${API_BASE}/api/v1/e2e_taxonomy/level2/${id}/status/`,
  //       {}
  //     );
  //     return res?.status || '';
  //   } catch (error) {
  //     console.error('Error checking status:', error);
  //     return '';
  //   }
  // }, []);

  // // Function to start polling
  // const startPolling = useCallback((id: string) => {
  //   console.log('Starting polling for ID:', id);

  //   // Clear any existing polling
  //   if (pollingIntervalRef.current) {
  //     clearInterval(pollingIntervalRef.current);
  //     pollingIntervalRef.current = null;
  //   }

  //   // Function to check and handle status
  //   const checkAndHandle = async () => {
  //     console.log('Checking status at:', new Date().toISOString());
  //     const status = await checkStatus(id);
  //     console.log('Current status:', status);

  //     if (status === 'completed') {
  //       console.log('Status is completed, stopping polling');
  //       // Stop polling
  //       if (pollingIntervalRef.current) {
  //         clearInterval(pollingIntervalRef.current);
  //         pollingIntervalRef.current = null;
  //       }

  //       // Refresh solutions
  //       try {
  //         const response = await fetchAISolution();
  //         if (response?.results?.length > 0) {
  //           setSolutionData(
  //             response.results.map(
  //               ({
  //                 id,
  //                 opportunity_name,
  //                 impact,
  //               }: {
  //                 id: string;
  //                 opportunity_name: string;
  //                 impact?: string;
  //               }) => ({
  //                 id,
  //                 opportunity_name,
  //                 impact: impact || null,
  //               })
  //             )
  //           );
  //           ShowToast({ message: 'AI Solutions refreshed successfully', type: ToastTypeEnum.Success });
  //         }
  //       } catch (error) {
  //         console.error('Error refreshing solutions:', error);
  //       } finally {
  //         setIsRegenerating(false);
  //       }
  //       setIsDiagramChanged(false);
  //     setIsSaved(false);
  //     setIsProcessDescriptionUpdated(false);
  //     setIsStepDescriptionUpdated(false);
  //     setIsDiagramEdited(false);

  //     setIsRegenerating(false);
  //     } else if (status === 'failed' || status === 'error') {
  //       console.log('Status is failed/error, stopping polling');
  //       // Stop polling on error
  //       if (pollingIntervalRef.current) {
  //         clearInterval(pollingIntervalRef.current);
  //         pollingIntervalRef.current = null;
  //       }
  //       setIsRegenerating(false);
  //       ShowToast({ message: 'AI Solutions regeneration failed', type: ToastTypeEnum.Error });
  //     } else {
  //       console.log('Status is still in progress, will check again in 5 seconds');
  //     }
  //   };

  //   // Start polling interval immediately (don't await first check)
  //   console.log('Setting up polling interval to check every 5 seconds');
  //   pollingIntervalRef.current = setInterval(checkAndHandle, 20000);

  //   // Also trigger first check immediately (but don't wait for it)
  //   checkAndHandle();
  // }, [checkStatus, fetchAISolution]);

  // const handleRegenerateAiSolutions = async () => {
  //   if (!selectedCard?.id || !diagramData?.diagram?.id || !diagramData?.diagram?.xml) {
  //     ShowToast({ message: 'Missing required data for regeneration', type: ToastTypeEnum.Error });
  //     return;
  //   }

  //   try {
  //     setIsRegenerating(true);

  //     // Step 1: Update status to in_progress
  //     const statusUpdatePayload = {
  //       status: "in_progress"
  //     };

  //     await httpService(
  //       httpConstants.METHOD_TYPE.POST,
  //       `${API_BASE}/api/v1/e2e_taxonomy/level2/${selectedCard.id}/status/`,
  //       statusUpdatePayload,
  //     );

  //     console.log('Status updated to in_progress');

  //     // Step 2: Prepare and trigger orchestrator API (don't wait for it)
  //     const regeneratePayload = {
  //       name: selectedCard.name,
  //       diagram : {
  //         xmlImage: diagramData.diagram.xml,
  //         id: diagramData.diagram.id,
  //         nodeDescription: diagramData.diagram.data || {},
  //         xmlData:""
  //       },
  //       process_id: selectedCard.id,
  //       description: taxonomySubprocessDescription || "",
  //       allProcesses: "",
  //       targetProcess: `${selectedE2E?.name || ''} -> ${selectedSubprocess?.name || ''} -> ${selectedCard?.name || ''}`,
  //       environment_type: ENVIRONMENT_TYPE,
  //       kpis: selectedCard.kpis || [],
  //     };

  //     console.log('Triggering orchestrator API with payload:', regeneratePayload);

  //     // Fire and forget - don't await this
  //     httpService(
  //       httpConstants.METHOD_TYPE.POST,
  //       'https://orchestrator.app.zbrain.ai/api/v1/webhooks/zirqd13drCA3R40iKAzRu/sync?tenantId=6764029052789d00255f17a1',
  //       regeneratePayload,
  //       {
  //         'Content-Type': 'application/json',
  //         'Authorization': 'Bearer za-U2FsdGVkX1/ttZ/pUsCjolOeYbHP4uWJKYu0O860+8yYE6ebW6DvrABLSug5Qkw4DIVv+2HmxvP3kq9t+WtL3P4Wt1KaaAc43piiuclp840Ek1C9ibGtQGD44w9fTXuK'
  //       },
  //       false
  //     ).then(response => {
  //       console.log('Orchestrator API completed:', response);
  //     }).catch(error => {
  //       console.error('Orchestrator API error:', error);
  //     });

  //     startPolling(selectedCard.id);

  //   } catch (error) {
  //     console.error('Error regenerating AI solutions:', error);
  //     ShowToast({ message: 'Failed to regenerate AI Solutions', type: ToastTypeEnum.Error });
  //     setIsRegenerating(false);
  //   }
  // };

  // Cleanup polling on unmount
  // useEffect(() => {
  //   return () => {
  //     if (pollingIntervalRef.current) {
  //       clearInterval(pollingIntervalRef.current);
  //       pollingIntervalRef.current = null;
  //     }
  //   };
  // }, []);

  const isSaveDisabled = !isDiagramChanged;
  console.log('isDiagraChange', isDiagramChanged);
  console.log('isSaveDisabled', isSaveDisabled);

  // Determine if Regenerate button should be enabled:
  // For diagram changes: only enable after save is complete
  // For process/step description changes: enable immediately
  // const isRegenerateEnabled = (isSaved && isDiagramEdited) ||
  //                            isProcessDescriptionUpdated ||
  //                            isStepDescriptionUpdated;

  // Function to determine if save buttons should be shown
  const shouldShowSaveButtons = () => {
    if (isProcessDiagram) {
      return isDiagramChanged;
    }

    if (!selectedCard || !selectedSubprocess) return false;

    const processType = selectedCard.processType || selectedSubprocess.processType;
    const currentTab = activeModal;

    if (processType === 'custom') {
      if (currentTab === 'diagram') {
        // Show save, discard, regenerate for process diagram
        return true;
      } else if (currentTab === 'processInfo') {
        // Show save, discard, regenerate for process info
        return true;
      } else if (currentTab === null && activeModal === null) {
        // If no modal is active (main view), show buttons based on diagram tab (this might be where we decide based on selectedItem in CustomDiagramBpnm)
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    const paletteContainer = document.querySelector('.djs-palette-entries');
    if (paletteContainer) {
      const allEntries = paletteContainer.querySelectorAll('.entry');
      paletteContainer.innerHTML = ''; // remove group wrappers
      allEntries.forEach((entry) => paletteContainer.appendChild(entry));
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          height: isProcessDiagram ? '100%' : '100vh',
          width: isProcessDiagram ? '100%' : isEditPermission ? 'auto' : '100%',
          position: isProcessDiagram ? 'relative' : 'fixed',
          top: isProcessDiagram ? 'auto' : isEditPermission ? 50 : 0,
          left: isProcessDiagram
            ? 'auto'
            : selectedCard?.processType === 'custom'
              ? isEditPermission
                ? 280
                : 0
              : 0,
          right: isProcessDiagram ? 'auto' : 0,
          bottom: isProcessDiagram ? 'auto' : 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#F4F6FF',
          transition: 'all 0.3s ease-in-out',
          // Fullscreen styles when expanded
          ...(isExpanded && {
            position: 'fixed !important',
            top: '0 !important',
            left: '0 !important',
            right: '0 !important',
            bottom: '0 !important',
            width: '100vw !important',
            height: '100vh !important',
            zIndex: 9999,
          }),
          // Mobile specific styles - full screen modal overlay
          ...(!isProcessDiagram &&
            isMobile && {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              backgroundColor: '#F4F6FF',
              overflow: 'hidden',
            }),
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flexDirection: 'column',
            display: 'flex',
            mt: isEditPermission ? 0 : 5,
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            borderRadius: { xs: 0, md: 2 },
            backgroundColor: 'background.paper',
            boxShadow: { xs: 'none', md: 1 },
            // overflow: 'hidden',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              backgroundColor: 'rgba(244, 246, 255, 0.95)',
              pt: isExpanded ? '20px' : 0,
              pr: 1,
              pl: 2,
              width: '100%',
              borderBottom: 'none',
              zIndex: 10,
              ...(isProcessDiagram && {
                overflow: 'visible',
              }),
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                height: 'auto',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                zIndex: 800,
                backgroundColor: 'rgba(244, 246, 255, 0.95)',
                mt: isProcessDiagram ? 8 : 1,
                mx: 'auto',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                pt: '24px',
              }}
            >
              {isProcessDiagram && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '24px', fontWeight: 500 }}>
                    {opportunityName || ' '}
                  </Typography>
                </Box>
              )}
              <Box>
                {(selectedTaxonomy || selectedE2E || selectedSubprocess) && (
                  <Box>
                    <Box sx={{ display: 'flex' }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: { xs: '100%', sm: 'auto' },
                          display: 'flex',
                          flexDirection: 'row',
                          borderRadius: 2,
                          ml: { xs: 0, sm: 2 },
                          px: { xs: 1, sm: 1.5 },
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            flexGrow: 1,
                          }}
                        >
                          {/* <Box
                  component="img"
                  src={diagramIcon}
                  alt="Diagram Icon"
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 2,
                    flexShrink: 0,
                    padding: 0.5,
                    ml: 2,
                    filter:
                      'brightness(0) saturate(100%) invert(44%) sepia(99%) saturate(1618%) hue-rotate(201deg) brightness(97%) contrast(86%)',
                  }}
                /> */}

                          <Box
                            sx={{
                              flexGrow: 1,
                              minWidth: 0,
                              mr: 0.5,
                              maxWidth: '100%',
                              display: '-webkit-box',
                              WebkitLineClamp: { xs: 2, sm: 'none' },
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: { xs: 'normal', sm: 'nowrap' },
                              '& .MuiTypography-root': {
                                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                textOverflow: { xs: 'ellipsis', sm: 'unset' },
                                maxWidth: '100%',
                              },
                            }}
                          >
                            <TaxonomyBreadcrumb
                              selectedTaxonomy={selectedTaxonomy}
                              selectedE2E={selectedE2E}
                              selectedSubprocess={selectedSubprocess}
                              selectedCard={selectedCard}
                              onResetAll={actualOnResetAll}
                              onResetTaxonomy={actualOnResetTaxonomy}
                              onResetE2E={actualOnResetE2E}
                              variant="diagram"
                              onEdit={() => setIsEditModalOpen(true)}
                              titleRight={
                                isMobile ? (
                                  <Box sx={{ ml: 1 }}>
                                    <CloseButton onClick={handleClose} />
                                  </Box>
                                ) : null
                              }
                              subtitleRight={
                                isMobile ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    {/* Save and Discard Buttons for Mobile */}
                                    {shouldShowSaveButtons() && (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          gap: 1,
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Button
                                          variant="outlined"
                                          onClick={handleClose}
                                          disabled={!shouldShowSaveButtons()}
                                          size="small"
                                          sx={{
                                            textTransform: 'none',
                                            height: 32,
                                            px: 1.5,
                                            fontSize: '12px',
                                            borderColor: '#1A55F9',
                                            color: '#1A55F9',
                                            '&:hover': {
                                              backgroundColor: '#F5F5F5',
                                              borderColor: '#1A55F9',
                                            },
                                            '&.Mui-disabled': {
                                              opacity: 0.5,
                                              borderColor: '#1A55F9 !important',
                                              color: '#1A55F9 !important',
                                              backgroundColor: 'transparent !important',
                                            },
                                          }}
                                        >
                                          Discard
                                        </Button>

                                        <Button
                                          variant="contained"
                                          onClick={isEditPermission ? handleSaveDiagram : undefined}
                                          disabled={!isEditPermission || isSaveDisabled}
                                          size="small"
                                          sx={{
                                            textTransform: 'none',
                                            height: 32,
                                            px: 2,
                                            fontSize: '12px',
                                            backgroundColor: '#1A55F9',
                                            color: '#FFFFFF',
                                            '&:hover': {
                                              backgroundColor: '#1A45C6',
                                            },
                                            '&.Mui-disabled': {
                                              opacity: 0.5,
                                              backgroundColor: '#1A55F9 !important',
                                              color: '#FFFFFF !important',
                                            },
                                          }}
                                        >
                                          Save
                                        </Button>
                                      </Box>
                                    )}
                                    <DiagramToolbar
                                      isEnterpriseModalOpen={isEnterpriseModalOpen}
                                      showAllAiComponents={showAllAiComponents}
                                      onEnterpriseClick={() => setIsEnterpriseModalOpen(true)}
                                      onToggleAiSteps={handleToggleAiSteps}
                                      selectedCard={selectedCard}
                                      onResetAll={actualOnResetAll}
                                      onClose={handleClose}
                                      onSaveDiagram={
                                        isEditPermission ? handleSaveDiagram : undefined
                                      }
                                      onAiSolutionsClick={() =>
                                        setActiveModal((prev) =>
                                          prev === 'aiSolution' ? null : 'aiSolution'
                                        )
                                      }
                                      onAddToSolutionXPLR={handleAddToSolutionXPLR}
                                      currentOrg={currentOrg}
                                      isSaveDisabled={isSaveDisabled}
                                      diagramData={diagramData}
                                      aiOpportunityData={aiOpportunityData}
                                      type={selectedTaxonomy ? 'taxonomy' : 'solutionxplr'}
                                      bpmnInstance={bpmnModelerRef.current}
                                      onApplyEnterprises={handleApplyEnterpriseComplete}
                                      onEdit={() => setIsEditModalOpen(true)}
                                      onProcessInfoClick={() =>
                                        setActiveModal((prev) =>
                                          prev === 'processInfo' ? null : 'processInfo'
                                        )
                                      }
                                      onDeleteClick={() => {
                                        setShowDeleteModal(true);
                                      }}
                                      isProcessDiagram={isProcessDiagram}
                                      // handleIdeationIntake={handleIdeationIntake}
                                      onRenameProcess={() => {
                                        setRenameProcessName(selectedCard?.name || '');
                                        setShowRenameModal(true);
                                      }}
                                      onDeleteIdeation={() => setShowDeleteIdeationModal(true)}
                                      onDownload={handleDownloadReport}
                                      loadingDocument={loadingDocument}
                                    />
                                  </Box>
                                ) : null
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    {/* <IconButton
                  sx={{
                    color: '#666',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                    },
                  }}
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton> */}
                    <DiagramInfoModal
                      isOpen={isEditModalOpen}
                      onClose={() => setIsEditModalOpen(false)}
                      selectedTaxonomy={taxonomySubprocessName}
                      selectedTaxonomyDescription={taxonomySubprocessDescription}
                      type="taxonomy"
                    />
                  </Box>
                )}
              </Box>

              {(selectedSolution || selectedStage) && (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    flexDirection: 'row',
                    borderRadius: 2,
                    ml: 2,
                    p: { xs: 1, sm: 1.5 },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                      flexGrow: 1,
                    }}
                  >
                    {/* <Box
                  component="img"
                  src={diagramIcon}
                  alt="Diagram Icon"
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 2,
                    flexShrink: 0,
                    padding: 0.5,
                    ml: 2,
                    filter:
                      'brightness(0) saturate(100%) invert(44%) sepia(99%) saturate(1618%) hue-rotate(201deg) brightness(97%) contrast(86%)',

                  }}
                /> */}

                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          mr: 0.5,
                          color: '#1b54f8',
                          fontSize: '1.5rem',
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: 500,
                          lineHeight: '100%',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Solution XPLR /
                      </Box>
                      <SolutionBreadcrumb
                        selectedSolution={selectedSolution}
                        selectedStage={selectedStage}
                        onResetAll={actualOnResetAll}
                        onResetSolution={actualOnResetSolution}
                        variant="diagram"
                      />
                    </Box>
                  </Box>

                  <IconButton
                    sx={{
                      color: '#666',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                      },
                    }}
                    onClick={() => setIsEditModalOpen(true)}
                  >
                    <EditIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </IconButton>
                  <DiagramInfoModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    selectedSolution={selectedSolution}
                    selectedSolutionDescription={selectedSolutionDescription}
                    type="solution"
                  />
                </Box>
              )}

              {/* Toolbar and Save/Discard Buttons */}
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    width: { xs: '100%', sm: 'auto' },
                    ml: 'auto',
                    pr: 3,
                    flexWrap: 'nowrap',
                    gap: 2,
                    mt: isExpanded ? 4 : 0,
                  }}
                >
                  {/* Save and Discard Buttons */}
                  {shouldShowSaveButtons() && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={!shouldShowSaveButtons()}
                        sx={{
                          textTransform: 'none',
                          height: 30,
                          px: 2,
                          borderColor: '#1A55F9',
                          color: '#1A55F9',
                          '&:hover': {
                            backgroundColor: '#F5F5F5',
                            borderColor: '#1A55F9',
                          },
                          '&.Mui-disabled': {
                            opacity: 0.5,
                            borderColor: '#1A55F9 !important',
                            color: '#1A55F9 !important',
                            backgroundColor: 'transparent !important',
                          },
                        }}
                      >
                        Discard
                      </Button>

                      <Button
                        variant="contained"
                        onClick={isEditPermission ? handleSaveDiagram : undefined}
                        disabled={!isEditPermission || isSaveDisabled}
                        sx={{
                          textTransform: 'none',
                          height: 30,
                          px: 3,
                          backgroundColor: '#1A55F9',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: '#1A45C6',
                          },
                          '&.Mui-disabled': {
                            opacity: 0.5,
                            backgroundColor: '#1A55F9 !important',
                            color: '#FFFFFF !important',
                          },
                        }}
                      >
                        Save
                      </Button>
                    </Box>
                  )}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: isProcessXPLRActive ? 1 : 0 }}
                  >
                    <DiagramToolbar
                      isEnterpriseModalOpen={isEnterpriseModalOpen}
                      showAllAiComponents={showAllAiComponents}
                      onEnterpriseClick={() => setIsEnterpriseModalOpen(true)}
                      onToggleAiSteps={handleToggleAiSteps}
                      selectedCard={selectedCard}
                      onResetAll={actualOnResetAll}
                      onClose={handleClose}
                      onSaveDiagram={isEditPermission ? handleSaveDiagram : undefined}
                      onAiSolutionsClick={() =>
                        setActiveModal((prev) => (prev === 'aiSolution' ? null : 'aiSolution'))
                      }
                      onAddToSolutionXPLR={handleAddToSolutionXPLR}
                      currentOrg={currentOrg}
                      isSaveDisabled={isSaveDisabled}
                      diagramData={diagramData}
                      aiOpportunityData={aiOpportunityData}
                      type={selectedTaxonomy ? 'taxonomy' : 'solutionxplr'}
                      bpmnInstance={bpmnModelerRef.current}
                      onApplyEnterprises={handleApplyEnterpriseComplete}
                      onEdit={() => setIsEditModalOpen(true)}
                      onProcessInfoClick={() =>
                        setActiveModal((prev) => (prev === 'processInfo' ? null : 'processInfo'))
                      }
                      onDeleteClick={() => {
                        setShowDeleteModal(true);
                      }}
                      isProcessDiagram={isProcessDiagram}
                      // handleIdeationIntake={handleIdeationIntake}
                      onRenameProcess={() => {
                        setRenameProcessName(selectedCard?.name || '');
                        setShowRenameModal(true);
                      }}
                      onDeleteIdeation={() => setShowDeleteIdeationModal(true)}
                      onDownload={handleDownloadReport}
                      loadingDocument={loadingDocument}
                      isProcessXPLRActive={isProcessXPLRActive}
                    />
                    {isProcessXPLRActive && selectedCard?.processType !== 'custom' && (
                      <IconButton
                        sx={{
                          '& button:hover': { backgroundColor: 'transparent' },
                          width: '40px',
                          height: '40px',
                        }}
                      >
                        <CloseButton onClick={handleClose} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Diagram Container */}
          <Box
            sx={{
              position: 'relative',
              flexGrow: 1,
              height: isExpanded ? '100%' : 'calc(100% - 100px)',
              overflow: 'hidden',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'flex-start',
              gap: 2,
              backgroundColor: 'rgba(244, 246, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              mx: 'auto',
              borderRadius: { xs: 0, md: 2 },
              zIndex: 100,
              '& .bjs-container': {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                outline: 'none !important',
                '& *': {
                  outline: 'none !important',
                },
                '& .djs-palette': {
                  position: 'absolute',
                  left: { xs: '8px', sm: '20px' },
                  top: { xs: '8px', sm: '70px' },
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  zIndex: 100,
                  maxHeight: 'calc(100vh - 200px)',
                  overflowY: 'auto',
                  outline: 'none !important',
                },
                '& .djs-container': {
                  border: 'none !important',

                  outline: 'none !important',
                },
                '& .djs-canvas': {
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '100%',
                  outline: 'none !important',
                  '& svg': {
                    width: '100% !important',
                    height: '100% !important',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    maxWidth: '100% !important',
                    maxHeight: '100% !important',
                    transition: 'all 0.3s ease-out',
                    outline: 'none !important',
                  },
                },
              },
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1100,
                }}
              >
                Loading Diagram...
              </Box>
            )}
            {error && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'error.main',
                  zIndex: 1100,
                }}
              >
                Error: {error}
              </Box>
            )}

            <Box
              ref={containerRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              sx={{
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none', // Prevent browser handling of touch events
                WebkitUserSelect: 'none',
                userSelect: 'none',
                flexGrow: 1,
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' },
                outline: 'none !important',
              }}
            />

            {/* AI Components Overlay */}
            {/* Hiding AI Components Overlay for now as these are not required (Remove hard-coded false to enable this feature)*/}
            {(showAllAiComponents || hoveredElements.size > 0) && false && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: '72px', sm: '0' },
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none',
                  zIndex: 1000,
                  boxSizing: 'border-box',
                }}
              >
                {bpmnModelerRef.current
                  ?.get<ElementRegistry>('elementRegistry')
                  .getAll()
                  .filter((element) => {
                    const allowedTypes = [
                      'bpmn:Task',
                      'bpmn:UserTask',
                      'bpmn:ServiceTask',
                      'bpmn:SendTask',
                      'bpmn:ReceiveTask',
                      'bpmn:ManualTask',
                      'bpmn:BusinessRuleTask',
                      'bpmn:ScriptTask',
                      'bpmn:Activity',
                      'bpmn:SubProcess',
                    ];
                    return (
                      allowedTypes.includes(element.type) &&
                      (showAllAiComponents || hoveredElements.has(element.id))
                    );
                  })
                  .map((element) => {
                    const gfx = bpmnModelerRef.current
                      ?.get<ElementRegistry>('elementRegistry')
                      .getGraphics(element);
                    if (gfx && containerRef.current) {
                      const elementBounds = gfx.getBoundingClientRect();
                      const containerBounds = containerRef.current.getBoundingClientRect();

                      const x = elementBounds.left - containerBounds.left;
                      const y = elementBounds.bottom - containerBounds.top;

                      return (
                        <DiagramAiComponentStepHover
                          key={element.id}
                          position={{
                            x: elementBounds.left - containerBounds.left,
                            y: elementBounds.bottom - containerBounds.top,
                          }}
                          style={{
                            position: 'absolute',
                            pointerEvents: 'auto',
                            left: `${x}px`,
                            top: `${y}px`,
                            transformOrigin: 'top left',
                            transition: 'all 0.3s ease-out',
                            willChange: 'transform, left, top',
                            minHeight: '180px',
                            maxHeight: '240px',
                            overflow: 'auto',
                          }}
                        />
                      );
                    }
                    return null;
                  })}
              </Box>
            )}

            {/* Modal Container */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                px: { xs: 0, sm: 3 },
                py: { xs: 0, sm: 2 },
                '& > *': {
                  pointerEvents: 'auto',
                  width: {
                    xs: '100%',
                    sm: '100%',
                    md: '600px',
                    lg: '600px',
                  },
                  margin: { xs: '0', md: '0' },
                },
              }}
            >
              {/* <Modal
                isOpen={isEnterpriseModalOpen}
                onClose={() => setIsEnterpriseModalOpen(false)}
                title="Hackett AI Hubble"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: { xs: '100vh', sm: 'calc(100vh - 140px)' },
                  borderRadius: { xs: 0, sm: '8px' },
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  mb: { xs: 0, sm: 2 },
                }}
                maxWidth='xl'
                width='1600px'
              >
                <TechnologyDataLandscape minContentHeight={"65vh"} maxContentHeight={"65vh"}/>
              </Modal> */}

              <CustomModal
                isOpen={!!selectedSolutionFromLibrary.id}
                onClose={() => setSelectedSolutionFromLibrary({ id: '', name: '' })}
                title={selectedSolutionFromLibrary.name || 'AI Solution'}
                maxWidth="xl"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: { xs: '100vh', md: 'calc(100vh - 140px)' },
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  mb: { xs: 0, sm: 2 },
                }}
                titleActions={
                  isMobile ? (
                    <IconButton onClick={() => handleToggleVault()}>
                      <img src="/images/Vault.png" alt="vault" style={{ width: 24, height: 24 }} />
                    </IconButton>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<img src="/images/Vault.png" alt="vault" />}
                      onClick={() => handleToggleVault()}
                    >
                      {`${selectedSolutionFromLibrary?.vault ? 'Remove from' : 'Add to'} My List`}
                    </Button>
                  )
                }
                agentResponse={aiSolutionData}

                //                   titleActions={
                //   selectedSolutionFromLibrary.id && (
                //     <SolutionActionButtons
                //       isFavorite={!!favoriteStatusMap[selectedSolutionFromLibrary.id]}
                //       onOpenFavoriteModal={() => {
                //         setFavoriteTarget({
                //           id: selectedSolutionFromLibrary.id,
                //           name: selectedSolutionFromLibrary.name,
                //           favorite: !!favoriteStatusMap[selectedSolutionFromLibrary.id],
                //         });
                //         setFavoriteModalOpen(true);
                //       }}
                //       onAddToSolutionXPLR={() => {
                //         // Close the modal and navigate to Solution XPLR with solution id
                //         setSelectedSolutionFromLibrary({ id: '', name: '' });
                //         navigate(`/solution-xplr?id=${selectedSolutionFromLibrary.id}`);
                //       }}
                //     />
                //   )
                // }
              >
                {/* <ViewModal selectedSolutionId={selectedSolutionFromLibrary.id} enterpriseContextApplied={enterpriseContextApplied} setEnterpriseContextApplied={setEnterpriseContextApplied}
                  // onAddToSolutionXPLR={() => {
                  //   handleClose();
                  //   setSelectedSolutionFromLibrary({ id: '', name: '' });
                  //   navigate(`/solution-xplr?id=${selectedSolutionFromLibrary.id}`);
                  // }}
                ></ViewModal> */}
                {dataSourceType === 'rest' && (
                  <ViewModal
                    selectedSolutionId={selectedSolutionFromLibrary.id}
                    enterpriseContextApplied={enterpriseContextApplied}
                    setEnterpriseContextApplied={setEnterpriseContextApplied}
                    aiSolutionData={aiSolutionData}
                    setAiSolutionData={setAiSolutionData}
                    diagramId={diagramData?.diagram?.id}
                  />
                )}

                {dataSourceType === 'graphql' && (
                  <ViewModalOld
                    selectedSolutionId={selectedSolutionFromLibrary.id}
                    enterpriseContextApplied={enterpriseContextApplied}
                    setEnterpriseContextApplied={setEnterpriseContextApplied}
                  />
                )}
              </CustomModal>
              {/* Unsaved Changes Confirmation Modal */}
              <ConfirmationModal
                isOpen={isNavigationGuardModalOpen || showUnsavedChangesModal}
                title="Save changes"
                description={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography sx={{ color: '#4f4f4f' }}>
                      You have unsaved changes. Would you like to save them before closing?
                    </Typography>
                  </Box>
                }
                primaryButtonText="Save Changes"
                secondaryButtonText="Discard Changes"
                onPrimaryAction={async () => {
                  await handleSaveDiagram();
                  setShowUnsavedChangesModal(false);

                  if (isNavigationGuardModalOpen) {
                    confirmNavigation();
                  } else {
                    if (onClose) {
                      onClose();
                    } else {
                      dispatch(closeDiagram());
                    }
                  }
                }}
                onSecondaryAction={() => {
                  setShowUnsavedChangesModal(false);

                  if (isNavigationGuardModalOpen) {
                    confirmNavigation();
                  } else {
                    if (onClose) {
                      const previousXml =
                        lastSavedXmlRef.current || diagramData?.diagram?.xml || '';
                      const previousData = prevDiagramData || diagramData?.diagram?.data || {};

                      if (!previousXml || !containerRef.current) {
                        return;
                      }

                      dispatch(
                        updateDiagramData({
                          DiagramData: {
                            diagram: {
                              id: diagramData?.diagram?.id,
                              xml: previousXml,
                              data: previousData,
                            },
                          },
                        })
                      );
                      onClose();
                    } else {
                      dispatch(closeDiagram());
                    }
                  }
                }}
                onClose={() => {
                  if (isNavigationGuardModalOpen) {
                    cancelNavigation();
                  } else {
                    setShowUnsavedChangesModal(false);
                  }
                }}
                width="626px"
                outerPadding={0}
              />

              <Modal
                isOpen={showVaultModal}
                onClose={handleCloseVaultModal}
                title={
                  selectedSolutionFromLibrary?.vault ? 'Remove from My List' : 'Add to My List'
                }
                maxWidth="sm"
              >
                <Box
                  sx={{
                    bgcolor: '#F9FBFF',
                    borderRadius: '12px',
                    p: 3,
                    display: 'flex',
                    alignItems: 'end',
                    justifyContent: 'stretch',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
                      Are you sure you want to{' '}
                      {selectedSolutionFromLibrary?.vault
                        ? 'remove this AI Solution from'
                        : 'add this AI Solution to'}{' '}
                      your my list?
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleToggleVault}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#2054F4',
                      borderRadius: '6px',
                      px: 3,
                      py: 1,
                      mt: 2,
                      '&:hover': { bgcolor: '#1A45C6' },
                      fontWeight: 'bold',
                    }}
                  >
                    {selectedSolutionFromLibrary?.vault ? 'Remove' : 'Add'}
                  </Button>
                </Box>
              </Modal>

              {/* Rename Process Modal */}
              <Modal
                isOpen={showRenameModal}
                onClose={() => setShowRenameModal(false)}
                title="Rename Process"
                maxWidth="sm"
                width="100%"
              >
                <Box
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: '14px', color: '#2c2c2c', mb: 1.5, fontWeight: 500 }}
                    >
                      Process Name
                    </Typography>
                    <input
                      type="text"
                      value={renameProcessName}
                      onChange={(e) => setRenameProcessName(e.target.value)}
                      placeholder="Enter process name"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '16px',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        fontFamily: 'Poppins, sans-serif',
                        boxSizing: 'border-box',
                        outline: 'none',
                        transition: 'border-color 0.3s',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1A55F9';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E0E0E0';
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        console.log('Process renamed to:', renameProcessName);
                        handleUpdateRenameProcess(renameProcessName);
                      }}
                      sx={{
                        textTransform: 'none',
                        bgcolor: '#2054F4',
                        borderRadius: '6px',
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: '14px',
                        '&:hover': { bgcolor: '#1A45C6' },
                      }}
                    >
                      Update
                    </Button>
                  </Box>
                </Box>
              </Modal>

              <DiagramModal
                isOpen={activeModal === 'diagram'}
                elementData={modal.elementData}
                activeTab={modal.activeTab}
                opportunities={modal.opportunities}
                onClose={handleModalClose}
                onTabChange={(tab) => setModal((prev) => ({ ...prev, activeTab: tab }))}
                onOpportunityAdd={handleOpportunityAdd}
                onStepDescriptionUpdate={handleStepDescriptionUpdate}
                diagramId={diagramData?.diagram?.id}
                diagramXml={diagramData?.diagram?.xml}
                bpmnModelerRef={bpmnModelerRef}
              />
            </Box>
            {/* Left zoom controls */}

            <Box
              sx={{
                ml: 4,
                position: 'absolute',
                bottom: { xs: 32, sm: 40 },
                left: { xs: 16, sm: 24 },
                zIndex: 1000,
                minWidth: 120,
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
                pointerEvents: 'auto',
              }}
            >
              <ZoomControls
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onFitView={handleFitViewToggle}
                isFitViewToggled={isFitViewToggled}
                onExpandToggle={handleExpandToggle}
                isExpanded={isExpanded}
              />
            </Box>
          </Box>
        </Box>

        <Slide direction="left" in={activeModal === 'aiSolution'} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              top: { xs: 0, sm: '15%' },
              right: 0,
              marginRight: { xs: 0, sm: '20px' },
              width: { xs: '100%', sm: 600 },
              maxWidth: { xs: '98%', md: '100%' },
              height: { xs: '100%', sm: '75vh' },
              bgcolor: 'background.paper',
              boxShadow: 4,
              p: { xs: 1, sm: 2 },
              zIndex: 1299,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              borderRadius: { xs: 0, sm: 1 },
              // Add mobile-specific styles
              ...(isMobile && {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999,
                borderRadius: 0,
                margin: 0,
              }),
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                // Mobile header styling
                ...(isMobile && {
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 1,
                  borderBottom: '1px solid #e0e0e0',
                  pb: 2,
                  mb: 0,
                }),
              }}
            >
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                AI Opportunities
              </Typography>
              <IconButton size="small" onClick={() => setActiveModal(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Content */}
            {solutionData.length > 0 ? (
              <DataTable
                columns={[
                  {
                    id: 'opportunity_name',
                    label: ' AI Opportunities',
                    minWidth: 200,
                    sortable: true,
                    clickable: true,
                  },
                  {
                    id: 'impact',
                    label: 'Impact',
                    minWidth: 150,
                    align: 'center',
                    sortable: true,
                    clickable: true,
                  },
                  {
                    id: 'actions',
                    label: 'Actions',
                    minWidth: 50,
                    align: 'center',
                    actions: { view: true },
                    clickable: true,
                  },
                ]}
                height="calc(100vh - 300px)"
                showRowBorders={true}
                rows={solutionData}
                onClick={(data: {
                  opportunity_name: string;
                  id: string;
                  impact: string;
                  vault?: boolean;
                }) => {
                  setSelectedSolutionFromLibrary({
                    id: data.id,
                    name: data.opportunity_name,
                    vault: data.vault,
                  });
                }}
                onView={(data: {
                  opportunity_name: string;
                  id: string;
                  impact: string;
                  vault?: boolean;
                }) => {
                  setSelectedSolutionFromLibrary({
                    id: data.id,
                    name: data.opportunity_name,
                    vault: data.vault,
                  });
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                  // Mobile loading state
                  ...(isMobile && {
                    flex: 1,
                    alignItems: 'center',
                    mt: 0,
                  }),
                }}
              >
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} /> Loading...
                </Typography>
                {/* <Typography color="text.secondary">No AI solutions found</Typography> */}
              </Box>
            )}
          </Box>
        </Slide>

        <ProcessInfoSlide
          open={activeModal === 'processInfo'}
          onClose={() => setActiveModal(null)}
          processDescription={taxonomySubprocessDescription}
          kpis={kpis}
          onUpdate={(description, updatedKpis) => {
            // Handle the update
            console.log('Updated description:', description);
            console.log('Updated KPIs:', updatedKpis);
            handleUpdateKPIs(description, updatedKpis);
          }}
        />

        {/* Save status notifications */}
        {/* <Snackbar open={savingDiagram} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="info" sx={{ width: '100%' }}>
            Saving diagram...
          </Alert>
        </Snackbar> */}

        {/* <Snackbar
          open={saveSuccess}
          autoHideDuration={3000}
          onClose={() => setSaveSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" sx={{ width: '100%' }}>
            BPMN saved successfully!
          </Alert>
        </Snackbar> */}

        <Snackbar
          open={!!saveError}
          autoHideDuration={5000}
          onClose={() => setSaveError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {saveError}
          </Alert>
        </Snackbar>

        <Snackbar
          open={opportunityUpdated}
          autoHideDuration={3000}
          onClose={() => setOpportunityUpdated(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="info" sx={{ width: '100%' }}>
            Opportunity updated
          </Alert>
        </Snackbar>

        <Modal
          isOpen={favoriteModalOpen}
          onClose={() => setFavoriteModalOpen(false)}
          title={
            favoriteTarget?.favorite
              ? 'Remove AI Solution from Favorites'
              : 'Add AI Solution to Favorites'
          }
          maxWidth="sm"
        >
          <Box
            sx={{
              bgcolor: '#F9FBFF',
              borderRadius: '12px',
              p: 3,
              display: 'flex',
              alignItems: 'end',
              justifyContent: 'stretch',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {favoriteTarget?.favorite ? (
                <Star sx={{ color: '#2054F4' }} />
              ) : (
                <StarBorder sx={{ color: '#2054F4' }} />
              )}
              <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
                Are you sure you want to{' '}
                {favoriteTarget?.favorite
                  ? 'remove this AI Solution from'
                  : 'add this AI Solution to'}{' '}
                your favorites?
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                if (!favoriteTarget) return;
                setFavoritePending(true);

                // Optimistic update
                setFavoriteStatusMap((prev) => ({
                  ...prev,
                  [favoriteTarget.id]: !prev[favoriteTarget.id],
                }));

                mutateFavorite({
                  variables: {
                    v1: {
                      id: favoriteTarget.id,
                      favorite: !favoriteTarget.favorite,
                    },
                  },
                });
              }}
              disabled={favoritePending}
              sx={{
                textTransform: 'none',
                bgcolor: '#2054F4',
                borderRadius: '6px',
                px: 3,
                py: 1,
                mt: 2,
                '&:hover': { bgcolor: '#1A45C6' },
                fontWeight: 'bold',
              }}
            >
              {favoritePending
                ? favoriteTarget?.favorite
                  ? 'Removing...'
                  : 'Adding...'
                : favoriteTarget?.favorite
                  ? 'Remove'
                  : 'Add'}
            </Button>
          </Box>
        </Modal>
        <Snackbar
          open={showApplySuccessSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowApplySuccessSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowApplySuccessSnackbar(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            AI Solution applied successfully
          </Alert>
        </Snackbar>
      </Box>
      <>
        {isMobile ? (
          <Box
            ref={wrapperRef}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100vh',
              bgcolor: '#fff',
              overflow: 'auto',
              zIndex: isEnterpriseModalOpen ? 9999 : -1,
              opacity: isEnterpriseModalOpen ? 1 : 0,
              pointerEvents: isEnterpriseModalOpen ? 'auto' : 'none',
              transition: 'opacity 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Hackett AI Hubble
              </Typography>
              <IconButton onClick={() => setIsEnterpriseModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <TechnologyDataLandscape
                minContentHeight="100%"
                maxContentHeight="100%"
                drawerContainer={wrapperRef.current}
              />
            </Box>
          </Box>
        ) : (
          <Modal
            isOpen={isEnterpriseModalOpen}
            onClose={() => setIsEnterpriseModalOpen(false)}
            title="Hackett AI Hubble"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: 'calc(100vh - 140px)',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              mb: 2,
            }}
            maxWidth="xl"
            width="1600px"
          >
            <TechnologyDataLandscape minContentHeight="65vh" maxContentHeight="65vh" />
          </Modal>
        )}
      </>
      <ConfirmationModal
        isOpen={isProcessDiagram ? showDeleteIdeationModal : showDeleteModal}
        title={`Delete Process`}
        description={
          isProcessDiagram ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ color: '#4f4f4f' }}>
                Are you sure you want to delete this entire ideation? This action cannot be undone.
                All associated data will be permanently removed.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ color: '#4f4f4f' }}>
                <Box>
                  Do you wish to delete this process? This action is permanent, please provide
                  confirmation to proceed.
                </Box>
              </Typography>
            </Box>
          )
        }
        primaryButtonText={
          <Box display="flex" alignItems="center" gap={1}>
            Delete
          </Box>
        }
        onPrimaryAction={isProcessDiagram ? handleConfirmDeleteIdeation : handleDeleteProcess}
        onClose={isProcessDiagram ? handleCloseDeleteIdeationModal : handleCloseDeleteModal}
        width={'781px'}
        outerPadding={0}
      />
      {/* Footer Actions */}
      <FooterActions
        onSave={isEditPermission && shouldShowSaveButtons() ? handleSaveDiagram : undefined}
        onDiscard={shouldShowSaveButtons() ? handleDiscardDiagramChanges : undefined}
        //onRegenerate={(selectedSubprocess?.processType === 'custom' || selectedCard?.processType === 'custom') && shouldShowSaveButtons() ? handleRegenerateAiSolutions : undefined}
        saveDisabled={!isEditPermission || isSaveDisabled}
        //regenerateDisabled={!(selectedSubprocess?.processType === 'custom' || selectedCard?.processType === 'custom') || isRegenerating || !isRegenerateEnabled}
        showButtons={shouldShowSaveButtons()}
      />
      <DownloadProgressSnackbar open={downloadProgressOpen} progress={downloadProgress} />
    </>
  );
};

export default DiagramBpmn;
